import React, { useState, useEffect, useRef } from 'react';
import { Grid, Card, Button, TextField as MuiTextField, Table, TableBody, TableCell, TableHead, TableRow, Toolbar, CardContent, Dialog, DialogContent, DialogActions, Typography, Radio, RadioGroup, FormControlLabel, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Form as FormBase, Field as FieldBase, FormSpy as FormSpyBase } from 'react-final-form';
import { getIn } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray as FieldArrayBase } from 'react-final-form-arrays';
import createDecorator from 'final-form-calculate';
import TextField from '../../components/text_field';
import Select from '../../components/fast_suggest_field';
import AutoComplete from '../../components/suggest_field';
import ExtendedSelect from '../../components/select';
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import DownloadButton from '../../components/download_button';
import axios from 'axios';
import Config from '../../config';
import Helpers, { nullableDateTemplate, getCompanyNames, getStatusOptions, getPartnerGroups } from '../../helpers';
import FileSaver from 'filesaver.js-npm';
import DropZone from './dropzone';
import { ShareDialog, DeleteDialog, SaveDialog, SaveAsDialog, loadProfiles, addExportProfile, saveProfile, Profile } from './import_export_profiles';
import isEqual from 'lodash.isequal';
import Moment from 'react-moment';
import Status from '../../components/status';
import DocType from '../../components/document_type';
import EnhancedTable from '../../components/table';
import SearchBox from '../../components/searchbox';
import moment from 'moment';
import { Alert } from '@mui/material';

// react-final-form + react-sortable-hoc are heavily used with custom component/
// render props; relaxing them to `any` keeps behavior identical and avoids
// fighting their generics across the form JSX (matches inventory/v1/search.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;
const FormSpy: any = FormSpyBase;
const FieldArray: any = FieldArrayBase;
const TypedEnhancedTable = EnhancedTable as React.ComponentType<any>;
// select.js default-exports a forwardRef component with untyped props; the
// inferred type rejects innerRef/variant/etc. Cast to a permissive component.
const ExtendedSelectAny = ExtendedSelect as React.ComponentType<any>;

interface FilterFieldOption {
    field?: string;
    operator?: string;
    value?: any;
    [key: string]: any;
}

interface FilterFieldDef {
    FieldTitle: string;
    FieldName: string;
    type: string;
}

interface OperatorOption {
    label: string;
    value: string;
}

interface ProfileFieldLike {
    field?: string;
    alias?: string;
    [key: string]: any;
}

const Draggable = SortableHandle(({children, style}: { children?: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ cursor: "move", ...style }}>{children}</div>
)) as React.ComponentType<{ children?: React.ReactNode; style?: React.CSSProperties }>;


const sortEnd = (move: (from: number, to: number) => void) => ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
  move(oldIndex, newIndex);
};

const sortStart = ({ node, helper }: any) => {
      node.childNodes.forEach((td: any, index: number) => {
        helper.childNodes[index].style.width = `${td.offsetWidth}px`;
      });
    }

const getTableContainer = () => document.getElementById("fieldTable");

const SampleDataField = ({input: {value}, sampleData}: any) => {
    if(sampleData[value]){
        return <Tooltip title={sampleData[value]} placement='left-start'><span>{sampleData[value]}</span></Tooltip>
    }
    return '';
}

const SortableItem = SortableElement(({ name, fields, value, options, sampleData }: any) => (
    <TableRow style={{backgroundColor: "white"}}>
    <TableCell padding="checkbox">
        <Draggable style={{display: 'flex', alignItems: 'center', width: '2em', fontSize: '16px'}}>{value + 1}</Draggable>
    </TableCell>
    <TableCell style={{minWidth: '10em', maxWidth: '30em'}}>
      <Field component={AutoComplete} hideUnmatched={true} options={options} style={{textAlign: 'left'}} name={`${name}.field`}/>
    </TableCell>
      <TableCell style={{minWidth: '10em', maxWidth: '30em'}}>
        <Field style={{textAlign: 'left', width: '100%'}} component={TextField} name={`${name}.alias`}/>
      </TableCell>
      <TableCell style={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: 0, minWidth: '10em'}}><Field component={SampleDataField} name={`${name}.field`} sampleData={sampleData}/></TableCell>
    <TableCell padding="none" style={{alignSelf: 'center'}}><Button variant='outlined' onClick={() => fields.remove(value)}>Remove</Button></TableCell>
    </TableRow>
)) as React.ComponentType<any>;

const SortableList = SortableContainer(({ items, options, sampleData }: any) => {
  return (
    <TableBody id="fieldTable">
      {items.map((name: string, index: number) => (
        <SortableItem
          key={`item-${index}`}
          index={index}
          value={index}
          name={name}
          fields={items}
          options={options}
          sampleData={sampleData}
        />
      ))}
    </TableBody>
  );
}) as React.ComponentType<any>;

const fieldNameMapping = {
    "OrderLines": "OrderLine",
    "InvoiceLines": "InvoiceLine",
    "ShipmentInfos": "ShipmentInfo",
    "ShipmentLines": "ShipmentLine",
    "Discounts": "Discount",
    "Taxes": "Tax",
    "ExtendedAttributes": "ExtendedAttribute"
};

const operators: { [type: string]: OperatorOption[] } = {
    strequal: [
        { label: "is", value: "eq" },
        { label: "is not", value: "neq" }
    ],
    partner: [
        { label: "is", value: "eq" },
        { label: "is not", value: "neq" },
        { label: "in group", value: "ingroup" }
    ],
    dateequal: [
        { label: "is", value: "eq" },
        { label: "is not", value: "neq" }
    ],
    daterange: [
        { label: "is", value: "eq" }
    ],
    date: [
        { label: "is", value: "eq" },
        { label: "is after", value: "gt" },
        { label: "is before", value: "lt" }
    ],
    equal: [
        { label: "is", value: "eq" },
        { label: "is not", value: "neq" }
    ],
    number: [
        { label: "is", value: "eq" },
        { label: "is not", value: "neq" },
        { label: "less than", value: "lt" },
        { label: "greater than", value: "gt" }
    ],
    partnerid: [
        { label: "is", value: "eq" },
        { label: "is not", value: "neq" },
        { label: "in group", value: "ingroup"}
    ]
};

const getOperators = (field: string, fields: FilterFieldDef[], hasGroups: boolean): OperatorOption[] => {
    if(!field){
        return [];
    }
    let fieldType = fields.find(r => r.FieldName.toLowerCase() === field.toLowerCase()).type;
    let ops = operators[fieldType];
    if(ops.filter(r => r.value === "ingroup") && !hasGroups){
        return ops.filter(r => r.value !== "ingroup");
    }
    return ops;
}

const filterFields: FilterFieldDef[] = [
    { FieldTitle: "Date Range", FieldName: "daterange", type: "daterange" },
    { FieldTitle: "Sender", FieldName: "SenderId", type: "partnerid" },
    { FieldTitle: "Receiver", FieldName: "ReceiverId", type: "partnerid" },
    { FieldTitle: "Status", FieldName: "Status", type: "strequal" },
    { FieldTitle: "Status Code", FieldName: "StatusCode", type: "number" },
    { FieldTitle: "SubStatus", FieldName: "SubStatus", type: "strequal" },
    { FieldTitle: "Reference Number", FieldName: "PartnerPO", type: "strequal" },
    { FieldTitle: "Logicbroker Key", FieldName: "LogicbrokerKey", type: "number" },
    { FieldTitle: "Page", FieldName: "Page", type: "equal" }
];

const parseDateRange = (dateRange?: string): (string | undefined)[] => {
    let ret: (string | undefined)[] = [undefined, undefined];
    if (dateRange){
        let split = dateRange.split(',').map(r => r.trim());
        ret[0] = parseRelativeDate(split[0]);
        if (split.length > 1 && split[1]){
            ret[1] = parseRelativeDate(split[1]);
        }
    }
    return ret;
}

const parseRelativeDate = (value: string): string => {
    let ret = undefined;
    if (value.startsWith('day')){
        ret = moment().startOf('day').add(value.substring(3), 'days').toISOString();
    }
    else if (value.startsWith('week')){
        ret = moment().startOf('week').add(value.substring(4), 'weeks').toISOString();
    }
    else if (value.startsWith('month')){
        ret = moment().startOf('month').add(value.substring(5), 'months').toISOString();
    } else {
        ret = moment(value).toISOString();
    }
    return ret;
}

const convertToFilterObject = (filter: FilterFieldOption[], partnerGroups?: any[]) => {
    var ret: any = {};
    var addedAdv = false;
    var findField = (field?: string) => filterFields.find(r => r.FieldName === field);
    for (const item of filter) {
        if (item.field === "Status" && item.operator === 'eq') {
            ret.status = item.value;
        } else if (item.field === "Page" && !isNaN(parseInt(item.value))) {
            ret.page = (parseInt(item.value) - 1);
        } else if (item.field === "from") {
            ret.from = moment(item.value).toISOString();
        } else if (item.field === "to") {
            ret.to = moment(item.value).toISOString();
        } else if (item.field === "daterange") {
            let dateRange = parseDateRange(item.value);
            ret.from = dateRange[0];
            ret.to = dateRange[1];
        } else {
            let str2 = item.field + " " + item.operator + " " + item.value;
            let fieldType = (findField(item.field) || {}).type;
            if (fieldType === "strequal") {
                str2 = item.field + " " + item.operator + " \"" + item.value + "\"";
            }
            if(item.operator === "ingroup"){
                let groupField = "name";
                if(fieldType === "partnerid"){
                    groupField = "id";
                }
                let companies = (((partnerGroups || []).filter(r => r.label === item.value)[0] || {}).value || []).map(r => r[groupField]);
                if(fieldType !== "partnerid"){
                    if(companies.length === 0){
                        companies = ["no partners"];
                    }
                    companies = companies.map(r => "\"" + r + "\"");
                } else if (companies.length === 0){
                    companies = [-1];
                }
                str2 = "(" + companies.map(r => item.field + " eq " + r).join(" or ") +")";
            }
            if (!addedAdv) {
                ret.advanced = str2;
                addedAdv = true;
            } else {
                ret.advanced += " and " + str2;
            }
        }
    }
    return ret;
}

const ProfileFields: { [key: string]: any[] } = {
    DefaultOrderFields: ["Identifier.LogicbrokerKey","DocumentDate","StatusCode", "PartnerPO", "SubTotal", "TaxTotal", "DiscountTotal", "HandlingAmount", "ShipMethod", "BillToAddress.CompanyName", "BillToAddress.Address1",
        "BillToAddress.Address2", "BillToAddress.City", "BillToAddress.Zip", "BillToAddress.State", "BillToAddress.Phone", "ShipToAddress.CompanyName", "ShipToAddress.FirstName", "ShipToAddress.LastName",
        "ShipToAddress.Address1", "ShipToAddress.Address2", "ShipToAddress.City", "ShipToAddress.Zip", "ShipToAddress.State", "ShipToAddress.Phone"],
    FTPOrderFields: [{field: "Identifier.LogicbrokerKey", alias: "Id"}, {field: "DocumentDate", alias: "Date"}, {field: "StatusCode"}, {field: "SenderCompanyId"}, {field: "PartnerPO"}, {field: "TotalAmount"}, {field: "ShipmentInfo.ClassCode", alias: "ShipMethod"}, {field: "BillToAddress.CompanyName"}, {field: "BillToAddress.FirstName"}, {field: "BillToAddress.LastName"}, {field: "BillToAddress.Address1"}, {field: "BillToAddress.Address2"}, {field: "BillToAddress.City"}, {field: "BillToAddress.Zip"}, {field: "BillToAddress.State"}, {field: "BillToAddress.Phone"}, {field: "ShipToAddress.CompanyName"}, {field: "ShipToAddress.FirstName"}, {field: "ShipToAddress.LastName"}, {field: "ShipToAddress.Address1"}, {field: "ShipToAddress.Address2"}, {field: "ShipToAddress.City"}, {field: "ShipToAddress.Zip"}, {field: "ShipToAddress.State"}, {field: "ShipToAddress.Phone"}, {field: "OrderLine.LineNumber", alias: "LineNumber"}, {field: "OrderLine.ItemIdentifier.SupplierSKU", alias: "ItemIdentifier.SupplierSKU"}, {field: "OrderLine.ItemIdentifier.PartnerSKU", alias: "ItemIdentifier.PartnerSKU"}, {field: "OrderLine.ItemIdentifier.UPC", alias: "ItemIdentifier.UPC"}, {field: "OrderLine.Description", alias: "Description"}, {field: "OrderLine.Quantity", alias: "Quantity"}, {field: "OrderLine.Price", alias: "Price"}, {field: "null1", alias: "TrackingNumber"}, {field: "null2", alias: "CarrierCode"}, {field: "null3", alias: "ShipFromAddress.CompanyName"}, {field: "null4", alias: "ShipFromAddress.Address1"}, {field: "null5", alias: "ShipFromAddress.Address2"}, {field: "null6", alias: "ShipFromAddress.City"}, {field: "null7", alias: "ShipFromAddress.State"}, {field: "null8", alias: "ShipFromAddress.Zip"}, {field: "null9", alias: "ShipFromAddress.Country"}, {field: "null10", alias: "QuantityShipped"}, {field: "Identifier.LinkKey", alias: "LinkKey"}],
    FTPShipmentFields: [{field: "Identifier.LogicbrokerKey", alias: "Id"}, {field: "DocumentDate", alias: "Date"}, {field: "StatusCode"}, {field: "SenderCompanyId"}, {field: "PartnerPO"}, {field: "TotalAmount"}, {field: "ShipmentInfo.ClassCode", alias: "ShipMethod"}, {field: "ShipmentInfo.TrackingNumber", alias: "TrackingNumber"}, {field: "BillToAddress.CompanyName"}, {field: "BillToAddress.FirstName"}, {field: "BillToAddress.LastName"}, {field: "BillToAddress.Address1"}, {field: "BillToAddress.Address2"}, {field: "BillToAddress.City"}, {field: "BillToAddress.Zip"}, {field: "BillToAddress.State"}, {field: "BillToAddress.Phone"}, {field: "ShipToAddress.CompanyName"}, {field: "ShipToAddress.FirstName"}, {field: "ShipToAddress.LastName"}, {field: "ShipToAddress.Address1"}, {field: "ShipToAddress.Address2"}, {field: "ShipToAddress.City"}, {field: "ShipToAddress.Zip"}, {field: "ShipToAddress.State"}, {field: "ShipToAddress.Phone"}, {field: "ShipmentLine.LineNumber", alias: "LineNumber"}, {field: "ShipmentLine.ItemIdentifier.SupplierSKU", alias: "ItemIdentifier.SupplierSKU"}, {field: "ShipmentLine.ItemIdentifier.PartnerSKU", alias: "ItemIdentifier.PartnerSKU"}, {field: "ShipmentLine.ItemIdentifier.UPC", alias: "ItemIdentifier.UPC"}, {field: "ShipmentLine.Description", alias: "Description"}, {field: "ShipmentLine.Quantity", alias: "QuantityShipped"}, {field: "ShipmentLine.Price", alias: "Price"}, {field: "ShipmentLine.ShipmentInfo.TrackingNumber", alias: "ItemTrackingNumber"}, {field: "ShipmentLine.ShipmentInfo.ClassCode", alias: "ItemShipMethod"}, {field: "ShipmentLine.ShipmentInfo.Qty", alias: "QuantityInBox"}, {field: "ShipFromAddress.CompanyName"}, {field: "ShipFromAddress.Address1"}, {field: "ShipFromAddress.Address2"}, {field: "ShipFromAddress.City"}, {field: "ShipFromAddress.State"}, {field: "ShipFromAddress.Zip"}, {field: "ShipFromAddress.Country"} ],
    FTPInvoiceFields: [{ field: "Identifier.LogicbrokerKey", alias: "Id" }, { field: "DocumentDate", alias: "Date" }, { field: "StatusCode" }, { field: "SenderCompanyId" }, { field: "PartnerPO" }, { field: "InvoiceNumber" }, { field: "InvoiceTotal" }, { field: "ShipmentInfo.ClassCode", alias: "ShipMethod" }, { field: "BillToAddress.CompanyName" }, { field: "BillToAddress.FirstName" }, { field: "BillToAddress.LastName" }, { field: "BillToAddress.Address1" }, { field: "BillToAddress.Address2" }, { field: "BillToAddress.City" }, { field: "BillToAddress.Zip" }, { field: "BillToAddress.State" }, { field: "BillToAddress.Phone" }, { field: "ShipToAddress.CompanyName" }, { field: "ShipToAddress.FirstName" }, { field: "ShipToAddress.LastName" }, { field: "ShipToAddress.Address1" }, { field: "ShipToAddress.Address2" }, { field: "ShipToAddress.City" }, { field: "ShipToAddress.Zip" }, { field: "ShipToAddress.State" }, { field: "ShipToAddress.Phone" }, { field: "InvoiceLine.LineNumber", alias: "LineNumber" }, { field: "InvoiceLine.ItemIdentifier.SupplierSKU", alias: "ItemIdentifier.SupplierSKU" }, { field: "InvoiceLine.ItemIdentifier.PartnerSKU", alias: "ItemIdentifier.PartnerSKU" }, { field: "InvoiceLine.ItemIdentifier.UPC", alias: "ItemIdentifier.UPC" }, { field: "InvoiceLine.Description", alias: "Description" }, { field: "InvoiceLine.Quantity", alias: "Quantity" }, { field: "InvoiceLine.Price", alias: "Price" }],
    FTPAckFields: [{ field: "Identifier.LogicbrokerKey", alias: "Id" }, { field: "DocumentDate", alias: "Date" }, { field: "StatusCode" }, { field: "SenderCompanyId" }, { field: "PartnerPO" }, { field: "BillToAddress.CompanyName" }, { field: "BillToAddress.FirstName" }, { field: "BillToAddress.LastName" }, { field: "BillToAddress.Address1" }, { field: "BillToAddress.Address2" }, { field: "BillToAddress.City" }, { field: "BillToAddress.Zip" }, { field: "BillToAddress.State" }, { field: "BillToAddress.Phone" }, { field: "ShipToAddress.CompanyName" }, { field: "ShipToAddress.FirstName" }, { field: "ShipToAddress.LastName" }, { field: "ShipToAddress.Address1" }, { field: "ShipToAddress.Address2" }, { field: "ShipToAddress.City" }, { field: "ShipToAddress.Zip" }, { field: "ShipToAddress.State" }, { field: "ShipToAddress.Phone" }, { field: "AckLines.LineNumber", alias: "LineNumber" }, { field: "AckLines.ItemIdentifier.SupplierSKU", alias: "ItemIdentifier.SupplierSKU" }, { field: "AckLines.ItemIdentifier.PartnerSKU", alias: "ItemIdentifier.PartnerSKU" }, { field: "AckLines.ItemIdentifier.UPC", alias: "ItemIdentifier.UPC" }, { field: "AckLines.Description", alias: "Description" }, { field: "AckLines.Quantity", alias: "Quantity" }, { field: "AckLines.QuantityCancelled", alias: "QuantityCancelled" }, { field: "AckLines.QuantityBackordered", alias: "QuantityBackordered" }, { field: "AckLines.Price", alias: "Price" }],
    DefaultShipmentFields: ["Identifier.LogicbrokerKey", "DocumentDate", "SenderCompanyId", "ReceiverCompanyId", "PartnerPO", "BillToAddress.CompanyName", "BillToAddress.Address1",
        "BillToAddress.Address2", "BillToAddress.City", "BillToAddress.Zip", "BillToAddress.State", "BillToAddress.Phone", "ShipToAddress.CompanyName", "ShipToAddress.FirstName", "ShipToAddress.LastName",
        "ShipToAddress.Address1", "ShipToAddress.Address2", "ShipToAddress.City", "ShipToAddress.Zip", "ShipToAddress.State", "ShipToAddress.Phone", "ShipFromAddress.CompanyName", "ShipFromAddress.FirstName",
        "ShipFromAddress.LastName", "ShipFromAddress.Address1", "ShipFromAddress.Address2", "ShipFromAddress.City", "ShipFromAddress.Zip", "ShipFromAddress.State", "ShipFromAddress.Phone",
        "ShipmentInfo.TrackingNumber", "ShipmentInfo.CarrierCode"
    ],
    DefaultInvoiceFields: ["Identifier.LogicbrokerKey", "DocumentDate", "SenderCompanyId", "ReceiverCompanyId", "PartnerPO", "BillToAddress.CompanyName", "BillToAddress.Address1",
        "BillToAddress.Address2", "BillToAddress.City", "BillToAddress.Zip", "BillToAddress.State", "BillToAddress.Phone", "ShipToAddress.CompanyName", "ShipToAddress.FirstName", "ShipToAddress.LastName",
        "ShipToAddress.Address1", "ShipToAddress.Address2", "ShipToAddress.City", "ShipToAddress.Zip", "ShipToAddress.State", "ShipToAddress.Phone", "ShipmentInfo.TrackingNumber", "ShipmentInfo.CarrierCode",
    "InvoiceDate", "InvoiceNumber", "InvoiceTotal", "PaymentTerm.TermsDescription", "PaymentTerm.PayInNumberOfDays"],
    DefaultAckFields: ["Identifier.LogicbrokerKey", "DocumentDate", "StatusCode", "PartnerPO", "BillToAddress.CompanyName", "BillToAddress.Address1",
        "BillToAddress.Address2", "BillToAddress.City", "BillToAddress.Zip", "BillToAddress.State", "BillToAddress.Phone", "ShipToAddress.CompanyName", "ShipToAddress.FirstName", "ShipToAddress.LastName",
        "ShipToAddress.Address1", "ShipToAddress.Address2", "ShipToAddress.City", "ShipToAddress.Zip", "ShipToAddress.State", "ShipToAddress.Phone"],
    OldOrderFields: [{field: "Identifier.LogicbrokerKey", alias:"Id"}, {field:"DocumentDate", alias:"Date"}, {field: "StatusCode"}, {field: "SenderCompanyId", alias: "CoId"}, {field: "ExtendedAttribute[Name=SourceSystem].Value", alias:"Source"}, "PartnerPO",
    "SubTotal", "TaxTotal", "DiscountTotal", "HandlingAmount", "TotalAmount", "ShipMethod",
    "BillToAddress.CompanyName", "BillToAddress.Address1", "BillToAddress.Address2", "BillToAddress.City",
    "BillToAddress.Zip", "BillToAddress.State", "BillToAddress.Phone", "ShipToAddress.CompanyName",
    "ShipToAddress.FirstName", "ShipToAddress.LastName", "ShipToAddress.Address1", "ShipToAddress.Address2",
    "ShipToAddress.City", "ShipToAddress.Zip", "ShipToAddress.State", "ShipToAddress.Phone"],
    OldOrderItemFields: [{field: "OrderLine.LineNumber", alias: "LineNumber"}, {field: "OrderLine.ItemIdentifier.SupplierSKU", alias:"ItemIdentifier.SupplierSKU"}, {field: "OrderLine.ItemIdentifier.PartnerSKU", alias: "ItemIdentifier.PartnerSKU"}, {field: "OrderLine.ItemIdentifier.UPC", alias: "ItemIdentifier.UPC"}, {field: "OrderLine.Description", alias:"Description"}, {field: "OrderLine.Quantity", alias:"Quantity"}, {field: "OrderLine.Price", alias:"Price"}],
    OldOrderExtraFields: [{field: "null1", alias: "TrackingNumber"}, {field: "null2", alias: "CarrierCode"}, {field: "null3", alias: "ShipFromAddress.CompanyName"}, {field: "null4", alias: "ShipFromAddress.Address1"}, {field: "null5", alias: "ShipFromAddress.Address2"}, {field: "null6", alias: "ShipFromAddress.City"}, {field: "null7", alias: "ShipFromAddress.State"}, {field: "null8", alias: "ShipFromAddress.Zip"}, {field: "null9", alias: "ShipFromAddress.Country"}],
    DefaultReturnFields: ["Identifier.LogicbrokerKey", "DocumentDate", "StatusCode", "PartnerPO", "ReturnNumber", "BillToAddress.CompanyName", "BillToAddress.Address1",
        "BillToAddress.Address2", "BillToAddress.City", "BillToAddress.Zip", "BillToAddress.State", "BillToAddress.Phone", "ShipToAddress.CompanyName", "ShipToAddress.FirstName", "ShipToAddress.LastName",
        "ShipToAddress.Address1", "ShipToAddress.Address2", "ShipToAddress.City", "ShipToAddress.Zip", "ShipToAddress.State", "ShipToAddress.Phone",
        "ReturnLines.LineNumber", "ReturnLines.ItemIdentifier.SupplierSKU", "ReturnLines.Quantity", "ReturnLines.Price", "ReturnLines.Description", "ReturnLines.ReturnReason", "ReturnLines.ReturnReasonCode" ],
}

class DocumentFields {
    CommonFields = ["OrderNumber", "PartnerPO", "SupplierPO", "OrderDate", "DocumentDate", "SenderCompanyId", "ReceiverCompanyId", "TotalAmount", "StatusCode", "HandlingAmount", "ExtendedAttribute[Name=ErpKey].Value"];
    ContactFields = ["CompanyName", "FirstName", "LastName", "Title", "Address1", "Address2", "City", "State", "Country", "Zip", "Province", "AddressCode", "StateCode", "CountryCode", "Phone", "Email"];
    OrderFields = ["RequestedShipDate", "DoNotShipBefore", "DoNotShipAfter", "ShipMethod", "TaxTotal", "DiscountTotal", "SubTotal"];
    InvoiceFields = ["InvoiceNumber", "InvoiceDate", "InvoiceTotal"];
    ShipmentFields = ["ShipmentDate", "ExpectedDeliveryDate", "InvoiceNumber", "BillofLading", "PRONumber", "ShipmentNumber"];
    AcknowledgementFields = ["OrderNumber", "PartnerPO", "OrderDate", "DocumentDate", "SenderCompanyId", "ReceiverCompanyId", "StatusCode", "Type", "AcknowledgementNumber", "ChangeReason", "ScheduledShipDate", "ExtendedAttribute[Name=Request Reference Number].Value"];
    ReturnFields = ["OrderNumber", "PartnerPO", "OrderDate", "DocumentDate", "SenderCompanyId", "ReceiverCompanyId", "TotalAmount", "StatusCode", "HandlingAmount", "ReturnNumber", "ReturnDate", "VendorNumber"];
    IdentifierFields = ["LogicbrokerKey", "LinkKey", "SourceKey"];
    ItemIdentifierFields = ["SupplierSKU", "PartnerSKU", "ManufacturerSKU", "UPC"];
    AckItemFields = ["Price", "ChangeReason", "Description", "Quantity", "QuantityUOM", "LineNumber", "QuantityCancelled", "QuantityBackordered", "Type"];
    ReturnItemFields = ["Price", "ReturnReason", "ReturnReasonCode", "Description", "Quantity", "QuantityUOM", "LineNumber"];
    ItemFields = ["Price", "Cost", "Description", "Quantity", "QuantityUOM", "LineNumber", "Weight", "WeightUOM", "ExtendedAttribute[Name=ItemType].Value"];
    OrderItemFields = ["ExtendedAttribute[Name=OrderQuantityShipped].Value"];
    PaymentTermFields = ["TermsDescription", "PayInNumberOfDays"];
    ShipmentInfoFields = ["DateShipped", "CarrierCode", "ClassCode", "SenderClassCode", "ReceiverClassCode", "ServiceLevelCode", "TrackingNumber", "ShipmentCost", "Qty", "ContainerCode", "ContainerType", "ShipmentContainerParentCode"];
    DiscountFields = ["DiscountCode", "DiscountName", "DiscountPercent", "DiscountAmount"];

    constructor(){
        this.Concat(this.ItemFields, "ShipmentInfo.", this.ShipmentInfoFields);
        this.Concat(this.ItemFields, "ItemIdentifier.", this.ItemIdentifierFields);
        this.Concat(this.AckItemFields, "ItemIdentifier.", this.ItemIdentifierFields);
        this.Concat(this.ReturnItemFields, "ItemIdentifier.", this.ItemIdentifierFields);
        this.Concat(this.CommonFields, "Identifier.", this.IdentifierFields);
        this.Concat(this.OrderFields, "BillToAddress.", this.ContactFields);
        this.Concat(this.OrderFields, "ShipToAddress.", this.ContactFields);
        this.Concat(this.OrderFields, "OrderLine.", this.ItemFields);
        this.Concat(this.OrderFields, "OrderLine.", this.OrderItemFields);
        this.Concat(this.InvoiceFields, "InvoiceLine.", this.ItemFields);
        this.Concat(this.InvoiceFields, "PaymentTerm.", this.PaymentTermFields);
        this.Concat(this.InvoiceFields, "ShipmentInfo.", this.ShipmentInfoFields);
        this.Concat(this.InvoiceFields, "Discount.", this.DiscountFields);
        this.Concat(this.InvoiceFields, "BillToAddress.", this.ContactFields);
        this.Concat(this.InvoiceFields, "ShipToAddress.", this.ContactFields);
        this.Concat(this.ShipmentFields, "ShipmentInfo.", this.ShipmentInfoFields);
        this.Concat(this.ShipmentFields, "ShipmentLine.", this.ItemFields);
        this.Concat(this.ShipmentFields, "BillToAddress.", this.ContactFields);
        this.Concat(this.ShipmentFields, "ShipToAddress.", this.ContactFields);
        this.Concat(this.ShipmentFields, "ShipFromAddress.", this.ContactFields);
        this.Concat(this.AcknowledgementFields, "BillToAddress.", this.ContactFields);
        this.Concat(this.AcknowledgementFields, "ShipToAddress.", this.ContactFields);
        this.Concat(this.AcknowledgementFields, "ShipFromAddress.", this.ContactFields);
        this.Concat(this.ReturnFields, "BillToAddress.", this.ContactFields);
        this.Concat(this.ReturnFields, "ShipToAddress.", this.ContactFields);
        this.Concat(this.ReturnFields, "ShipFromAddress.", this.ContactFields);
        this.Concat(this.ReturnFields, "OrderedByAddress.", this.ContactFields);
        this.Concat(this.ShipmentFields, "", this.CommonFields);
        this.Concat(this.OrderFields, "", this.CommonFields);
        this.Concat(this.InvoiceFields, "", this.CommonFields);
        this.Concat(this.AcknowledgementFields, "Identifier.", this.IdentifierFields);
        this.Concat(this.AcknowledgementFields, "AckLines.", this.AckItemFields);
        this.Concat(this.ReturnFields, "ReturnLines.", this.ReturnItemFields);
        this.Concat(this.ReturnFields, "Identifier.", this.IdentifierFields);
        this.OrderFields.sort();
        this.InvoiceFields.sort();
        this.ShipmentFields.sort();
        this.AcknowledgementFields.sort();
        this.ReturnFields.sort();
    }

    Concat(arr: string[], prefix: string, arr2: string[]) {
        for (const item of arr2) {
            arr.push(prefix + item);
        }
    }

    getFieldOptions = (docType?: string) => {
        if(!docType){
            return [];
        }
        let fields: string[] = (this as any)[docType + "Fields"];
        return fields.map(r => { return {value: r, label: r}});
    }
}



const getDefaultProfiles = () => {
    let profiles: any[] = [];
    addExportProfile(profiles, "Default Order Export", ProfileFields.DefaultOrderFields, 'Order');
    addExportProfile(profiles, "Default FTP Order Export", ProfileFields.FTPOrderFields, 'Order');
    addExportProfile(profiles, "Default Shipment Export", ProfileFields.DefaultShipmentFields, 'Shipment');
    addExportProfile(profiles, "Default FTP Shipment Export", ProfileFields.FTPShipmentFields, 'Shipment');
    addExportProfile(profiles, "Default Invoice Export", ProfileFields.DefaultInvoiceFields, 'Invoice');
    addExportProfile(profiles, "Default FTP Invoice Export", ProfileFields.FTPInvoiceFields, 'Invoice');
    addExportProfile(profiles, "Default Ack. Export", ProfileFields.DefaultAckFields, 'Acknowledgement');
    addExportProfile(profiles, "Default FTP Ack. Export", ProfileFields.FTPAckFields, 'Acknowledgement');
    addExportProfile(profiles, "Orders With Item Detail", ProfileFields.OldOrderFields.concat(ProfileFields.OldOrderItemFields).concat(ProfileFields.OldOrderExtraFields), "Order");
    addExportProfile(profiles, "Orders Without Item Detail", ProfileFields.OldOrderFields.concat(ProfileFields.OldOrderExtraFields), "Order");
    addExportProfile(profiles, "Default Return Export", ProfileFields.DefaultReturnFields, 'Return');
    return profiles;
}

const loadExportProfiles = async () => {
    return await loadProfiles("Export", getDefaultProfiles());
}


export { operators, getOperators, loadExportProfiles as loadProfiles};

const IfSet = ({ field, children }: { field: string; children: any }) => (
  <Field name={field} subscription={{ value: true }}>
    {({ input: { value } }: any) => (!(!value) ? typeof children === 'function' ? children({value}) : children : null)}
  </Field>
)

const dateOptions = [
    {label: "Custom", value: "custom"},
    {label: "Today", value: "day 0"},
    {label: "Yesterday", value: "day -1,day 0"},
    {label: "Last 3 days", value: "day -3"},
    {label: "Last 7 days", value: "day -7"},
    {label: "Last 14 days", value: "day -14"},
    {label: "Last 30 days", value: "day -30"},
    {label: "This week", value: "week 0"},
    {label: "Last week", value: "week -1,week 0"},
    {label: "This month", value: "month 0"},
    {label: "Last month", value: "month -1,month 0"}
]

const FilterValueField = ({name, fieldName, partnerOptions, statusOptions}: any) => {
    if(fieldName === "ReceiverId" || fieldName === "SenderId"){
        return <Field component={AutoComplete} style={{width: '100%'}} name={`${name}.value`} options={partnerOptions}/>;
    }
    if(fieldName === "Status"){
        return <Field component={AutoComplete} style={{width: '100%'}} name={`${name}.value`} options={statusOptions}/>;
    }
    if(fieldName === "from" || fieldName === "to"){
        return <Field component={TextField} style={{width: '100%'}} name={`${name}.value`} type="date" />;
    }
    if(fieldName === "daterange"){
        return <div style={{display: 'flex'}}>
            <Field component={Select} style={{width: '100%'}} name={`${name}.dateType`} options={dateOptions} />
            <Field name={`${name}.dateType`} subscription={{ value: true }}>
                {({ input: { value } }) => {
                    if (value === "custom") {
                        return <>
                            <Typography style={{alignContent: 'center', fontWeight: 'bold', paddingLeft: '1em', paddingRight: '0.5em'}}>Start:</Typography>
                            <Field name={`${name}.dateTo`} subscription={{ value: true }}>
                                {({ input: { value: dateTo } }) =>
                                <Field component={TextField} style={{width: '100%'}} name={`${name}.dateFrom`} type="date" inputProps={{max: dateTo}} />
                            }</Field>
                            <Typography style={{alignContent: 'center', fontWeight: 'bold', paddingLeft: '1em', paddingRight: '0.5em'}}>End:</Typography>
                            <Field name={`${name}.dateFrom`} subscription={{ value: true }}>
                                {({ input: { value: dateFrom } }) =>
                                <Field component={TextField} style={{width: '100%'}} name={`${name}.dateTo`} type="date" inputProps={{min: dateFrom}} />
                            }</Field>
                        </>;
                    }
                    return <>
                        <Typography color='textSecondary' style={{alignContent: 'center', paddingLeft: '1em', paddingRight: '1em'}}>
                            <IfSet field={`${name}.dateFrom`}>{({value}) => moment(value).format('MM/DD/YYYY')}</IfSet>
                            <span style={{paddingRight: '0.5em', paddingLeft: '0.5em'}}>-</span>
                            <IfSet field={`${name}.dateTo`}>{({value}) => moment(value).format('MM/DD/YYYY')}</IfSet>
                        </Typography>
                    </>
                }}
            </Field>
        </div>;
    }
    return <Field component={TextField} style={{width: '100%'}} name={`${name}.value`} />;
};

const getDownloadLink = async (docType: string, filter?: FilterFieldOption[], fields?: ProfileFieldLike[], fileType?: string, lbKeys?: (string | number)[]) => {
    let url = Config.api + `/api/v2/${docType}s/export`;
    let partnerGroups = await getPartnerGroups();
    let req = {
        FileType: fileType,
        Columns: fields ? fields.map(r => ({SourceField: r.field, TargetField: r.alias || r.field})) : null,
        Filter: filter ? convertToFilterObject(filter, partnerGroups) : null,
        LogicbrokerKeys: lbKeys ? lbKeys.map(r => r.toString()) : null
    };
    try{
        let link = (await axios.post(url, req)).data.Body;
        return link;
    }catch(e){
        let error = 'An unexpected error occurred.';
        if(e.response && e.response.data){
            var data = e.response.data;
            try{
                data = JSON.parse(await new Helpers().readBlob(data));
            }catch{
                // do nothing
            }
            error = new Helpers().getApiErrors(data).join("\n")
        }
        throw new Error(error);
    }
}

interface AdvancedExportProps {
    setTitle?: (title: string) => void;
    isRetailer?: boolean;
    coid?: number | string;
    companyName?: string;
}

const blankProfile: Profile = {Name: '', DocumentType: 'Order',  Filter: [{field: 'Status', operator: 'eq', value: "Ready to Ship"}], Fields: []};

const documentTypes = ['Order', 'Invoice', 'Shipment', 'Acknowledgement', 'Return'];

const exportTypes = [
    {label: "CSV", value: "csv"},
    {label: "XLSX", value: "xlsx"},
    {label: "XML", value: "xml"},
    {label: "JSON", value: 'json'},
    {label: "External XML", value: 'legacy'}
];

// Converted from a class component to an idiomatic function component. State is a
// single object updated with a functional merging setState; handlers read current
// values via closure, cross-async re-selects take the reloaded profiles as an
// argument, and the compare-then-set cases (onDocTypeChange, the DocumentType
// decorator) use a functional updater -- so no state-mirror ref is needed. The
// instance caches (documentStatuses, defaultProfiles, documentFields) and the
// once-created final-form decorators keep their genuine refs.
function AdvancedExport(props: AdvancedExportProps) {
    const [state, setStateRaw] = useState<any>({profile: blankProfile, profiles: [], statusOptions: [], fileType: 'csv', showSaveAs: false, showImport: false,
    confirmDelete: false, showError: false, showShare: false, showSave: false, documentFieldOptions: [], showDocumentPicker: false,
    term: '', termValue: '', sampleData: {}, showDownloading: false, partnerGroups: []});
    const setState = (partial: any) => setStateRaw((prev: any) => ({ ...prev, ...partial }));
    const profileSelectRef = useRef<any>(null);
    const documentStatusesRef = useRef<{ [docType: string]: any }>({});
    const defaultProfilesRef = useRef<Profile[] | undefined>(undefined);
    const documentFieldsRef = useRef<DocumentFields | undefined>(undefined);
    if(!documentFieldsRef.current){
        documentFieldsRef.current = new DocumentFields();
    }

    const isDefault = (name?: string) => {
        if(!defaultProfilesRef.current){
            defaultProfilesRef.current = getDefaultProfiles();
        }
        return defaultProfilesRef.current.filter(r => r.Name === name).length > 0;
    };

    const handleSubmit = async (e) => {
        try{
            let link = await getDownloadLink(e.DocumentType, e.Filter, e.Fields, state.fileType);
            let fileType = (state.fileType === "legacy" ? "xml" : state.fileType) || 'csv';
            if(fileType === 'xml' || fileType === 'json'){
                fileType = 'zip';
            }
            let a = document.createElement('a');
            a.download = 'export.' + fileType;
            a.href = link;
            a.target = '_blank';
            a.click();
            setState({showDownloading: true});
        }catch(ex){
            setState({showError: true, error: ex.message});
        }
    };

    const reloadProfiles = async (loadPartners?: boolean) => {
        let profiles = await loadExportProfiles();
        let partnerGroups = await getPartnerGroups();
        let newState: any = {profiles, partnerGroups, partnerGroupOptions: partnerGroups.map(r => ({label: r.label, value: r.label}))};
        if(loadPartners){
            let partners = (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners;
            let partnerOptions = partners.map(r => {return {value: r.Id, label: r.CompanyName}});
            partnerOptions.push({value: props.coid, label: props.companyName});
            partnerOptions = partnerOptions.sort((a, b) => a.label.localeCompare(b.label));
            newState.partnerOptions = partnerOptions;
        }
        setState(newState);
        return profiles;
    };

    const getFieldOperators = (field: string) => {
        return getOperators(field, filterFields, state.partnerGroups.length > 0);
    };

    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Advanced Export');
        }
        reloadProfiles(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onProfileSelect = (name) => {
        if(!name){
            return;
        }
        let profile = state.profiles.find(r => r.Name === name);
        // backwards compatibility for old profiles
        if (profile && profile.Filter && profile.Filter.filter(r => r.field === "from" || r.field === "to").length > 0){
            let from = (profile.Filter.filter(r => r.field === "from")[0] || {}).value;
            let to = (profile.Filter.filter(r => r.field === "to")[0] || {}).value;
            let newFilters = profile.Filter.filter(r => r.field !== "from" && r.field !== "to");
            newFilters.push({field: "daterange", operator: "eq", value: `${from},${to}`});
            profile.Filter = newFilters;
        }
        if (profile && profile.Filter) {
            profile.Filter.forEach(filter => {
                if(filter.field === "daterange"){
                    let parsed = parseDateRange(filter.value);
                    filter.dateType = (dateOptions.find(r => r.value === filter.value) || {}).value || 'custom';
                    filter.dateFrom = (parsed[0] || '').split('T')[0];
                    filter.dateTo = (parsed[1] || moment().toISOString()).split('T')[0];
                }
            });
        }
        setState({profile});
    };

    const getProfile = (values?: any) => {
        values = values || state.profile;
        var profile: Profile = {};
        profile.DocumentType = values.DocumentType;
        profile.Filter = values.Filter;
        profile.Fields = values.Fields.map((r: any) => {return {field: r.field, id: r.index, alias: r.alias}});
        return profile;
    };

    const save = async (values) => {
        await saveProfile(values, 'Export');
        await onSave();
    };

    const onSave = async () => {
        await reloadProfiles();
        setState({showSave: false});
    };

    const onSaveAs = async (newName) => {
        let profiles = await reloadProfiles();
        profileSelectRef.current.setValue(newName);
        setState({showSaveAs: false, profile: profiles.find(r => r.Name === newName)});
    };

    const showSave = (values) => {
        if(!values.Id){
            save(values);
        } else {
            setState({showSave: true});
        }
    };

    const showSaveAs = () => {
        setState({showSaveAs: true});
    };

    const exportProfileFile = (values) => {
        var profile = getProfile(values);
        var config = JSON.stringify(profile);
        var fileName = 'export-profile.json';
        if(state.profile && state.profile.Name){
            fileName = `export-profile-${state.profile.Name}.json`;
        }
        var blob = new Blob([config], { type: "application/octet-stream" });
        (FileSaver as any).saveAs(blob, fileName);
    };

    const openImportDialog = () => {
        setState({showImport: true, newProfileName: '', importFile: null, disableImport: false});
    };

    const onLoad = async (reader, name) => {
        var text = reader.result;
        let parsed = JSON.parse(text);
        parsed.Name = name;
        await saveProfile(parsed, 'Export');
        await onSaveAs(name);
        setState({showImport: false});
    };

    const importFile = (name, file) => {
        setState({disableImport: true});
        var reader = new FileReader();
        reader.onload = onLoad.bind(null, reader, name);
        reader.readAsText(file);
    };

    const onDrop = (files) => {
        if(files && files.length > 0){
            setState({importFile: files[0]});
        }
    };

    const onDelete = async () => {
        await reloadProfiles();
        setState({profile: blankProfile, confirmDelete: false});
        profileSelectRef.current.setValue('');
    };

    const shared = async (name) => {
        let profiles = await reloadProfiles();
        setState({profile: profiles.find(r => r.Name === name), showShare: false});
    };

    const getDocumentStatuses = async (docType) => {
        if(!documentStatusesRef.current[docType]){
            let statuses = (await axios.get(Config.api + `/api/v1/statuses/${docType}`)).data.Body.DocumentTypeStatuses;
            documentStatusesRef.current[docType] = statuses.map(r => r.StatusDescription).sort().map(r => ({label: r, value: r}));
        }
        return documentStatusesRef.current[docType];
    };

    const onDocTypeChange = async (docType) => {
        if(documentTypes.indexOf(docType) > -1){
            let statuses = await getDocumentStatuses(docType);
            setStateRaw((prev: any) => isEqual(statuses, prev.statusOptions) ? prev : {...prev, statusOptions: statuses});
        }
    };

    const decoratorsRef = useRef<any>(null);
    if(!decoratorsRef.current){
        decoratorsRef.current = {
            documentTypeChecker: createDecorator({
                field: /DocumentType/,
                updates: (value, name, allValues) => {
                    let documentFieldOptions = documentFieldsRef.current!.getFieldOptions(value);
                    setStateRaw((prev: any) => isEqual(prev.documentFieldOptions, documentFieldOptions) ? prev : {...prev, documentFieldOptions});
                    return {};
                }
            }),
            filterValueChecker: createDecorator({
                field: /operator/,
                updates: (value, name, allValues, prevValues) => {
                    let oldValue = getIn(prevValues, name);
                    let fieldValueName = name.replace('.operator', '.value');
                    let fieldValue = getIn(allValues, fieldValueName);
                    if((value === "ingroup" && fieldValue && !(typeof fieldValue === "string"))
                    || (oldValue === "ingroup" && fieldValue)){
                        return {
                            [fieldValueName]: ""
                        }
                    }
                    return {};
                }
            }),
            dateCalculator: createDecorator({
                field: /field/,
                updates: (value, name, allValues, prevValues) => {
                    let filterName = name.replace('.field', '');
                    if(value !== 'daterange' && getIn(prevValues, name) === 'daterange'){
                        return {[filterName]: {
                                field: value,
                                operator: 'eq'
                        }};
                    }
                    else if (value === 'daterange' && getIn(prevValues, name) !== 'daterange'){
                        let newFilter: any = {
                                field: value,
                                operator: 'eq',
                                dateType: 'custom'
                            };
                        let filterValue = getIn(allValues, filterName + '.value');
                        if(!getIn(prevValues, name) && filterValue){
                            let parsed = parseDateRange(filterValue);
                            newFilter.dateType = (dateOptions.find(r => r.value === filterValue) || {}).value || 'custom';
                            newFilter.dateFrom = (parsed[0] || '').split('T')[0];
                            newFilter.dateTo = (parsed[1] || moment().toISOString()).split('T')[0];
                        }
                        return {[filterName]: newFilter};
                    }
                    return {};
                }
            },
            {
                field: /dateType/,
                updates: (value, name, allValues, prevValues) => {
                    let filterName = name.replace('.dateType', '');
                    if (value && value !== 'custom'){
                        let parsed = parseDateRange(value);
                        return {
                            [filterName + '.value']: value,
                            [filterName + '.dateFrom']: (parsed[0] || '').split('T')[0],
                            [filterName + '.dateTo']: (parsed[1] || moment().toISOString()).split('T')[0]
                        }
                    }
                    return {};
                }
            },
            {
                field: /dateFrom|dateTo/,
                updates: (value, name, allValues) => {
                    let filterName = name.substring(0, name.lastIndexOf('.'));
                    if (getIn(allValues, filterName + '.dateType') === 'custom'){
                        let from = getIn(allValues, filterName + '.dateFrom');
                        let to = getIn(allValues, filterName + '.dateTo');
                        return {
                            [filterName + '.value']: from || to ? `${from},${to}` : ''
                        }
                    }
                    return {};
                }
            }
            )
        };
    }
    const documentTypeChecker = decoratorsRef.current.documentTypeChecker;
    const filterValueChecker = decoratorsRef.current.filterValueChecker;
    const dateCalculator = decoratorsRef.current.dateCalculator;

    const addFieldsFromDocument = (updateFieldListMutator: any, values?: any) => {
        let existingFields = ((values || {}).Fields || []).map((r: any) => r.field);;
        setState({showDocumentPicker: true, term: '', termValue: '', existingFields, updateFieldList: updateFieldListMutator});
    };

    const updateFieldsFromDocument = async (id: any, type: string) => {
        let doc = (await axios.get(Config.api + `/api/v1/${type}s/${id}`)).data.Body;
        if(Object.keys(doc).length === 1){
            doc = doc[Object.keys(doc)[0]];
        }
        let fieldData = getFieldData(doc);
        let sampleData = fieldData.reduce((acc: any, r: any) => {
            acc[r.name] = r.sample;
            return acc;
        }, {});
        let allFields = fieldData.map(r => r.name).sort();
        let fieldsToAdd = allFields.filter(r => state.existingFields.indexOf(r) === -1);
        state.updateFieldList(fieldsToAdd.map(r => ({field: r, alias: ''})));
        setState({showDocumentPicker: false, sampleData: sampleData});
    };

    const updateFieldList = ([newFields]: any[], formState: any, { changeValue }: any) => {
       changeValue(formState, "Fields", (value: any[]) => ([...value, ...newFields]));
    };

    const getFieldData = (obj: any, parent?: string): any[] => {
        if(!(obj instanceof Object)){
            return [];
        }
        let keys = Object.keys(obj);
        let ret: any[] = [];
        keys.forEach(key => {
            if(obj[key] && obj[key] !== "0001-01-01T00:00:00" && obj[key] !== "0001-01-01"){
                let mappedKey = fieldNameMapping[key] || key;
                if (obj[key] instanceof Array){
                    if(key === "ExtendedAttributes"){
                        let keys = obj[key].map(r => ({name: r.Name, sample: r.Value}));
                        mappedKey = parent === "ShipmentInfos" ? key : mappedKey;
                        ret = [...ret, ...keys.map(r => ({name: `${mappedKey}[Name=${r.name}].Value`, sample: r.sample}))];
                    }else{
                        let subNames = obj[key].map(r => getFieldData(r, key)).flat(1);
                        ret = [...ret, ...subNames.filter((r, i) => subNames.findIndex(x => x.name === r.name) === i).map(r => ({name: mappedKey + "." + r.name, sample: r.sample}))];
                    }
                } else if(obj[key] instanceof Object){
                    let subNames = getFieldData(obj[key], key);
                    ret = [...ret, ...subNames.map(r => ({name: mappedKey + "." + r.name, sample: r.sample}))];
                } else {
                    ret.push({name: mappedKey, sample: obj[key]});
                }
            }
        });
        return ret;
    }

    const getSearchConfig = (docType: string) => {
        return {
            url: Config.api + `/odata/Company/Functions.DocumentSearch`,
            columns: [
              { id: "id", hidden: true},
              { id: 'status', hidden: true, label: 'Status Code' },
              { id: 'internalId', type: 'number', filterable: true, sortable: true, numeric: true, disablePadding: true, label: 'ID#', width: '4em' },
              { id: 'partnerPo', filterable: true, label: 'Reference Number', width: '11em'},
              { id: 'sourceKey', filterable: true, label: 'Source Key', hidden: true, toggleable: true },
              { id: 'linkKey', filterable: true, hidden: true, toggleable: true, label: 'Link Key' },
              { id: 'date', type:'date', filterable: true, sortable: true, label: 'Date', template: (value) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '12em' },
              { id: 'requestedShipDate', type:'localdate', filterable: true, sortable: true, label: 'Requested Ship Date', template: nullableDateTemplate, hidden: true, toggleable: true },
              { id: 'documentType', hidden: true, filterable: true, label: 'Type', template: (value) => <DocType docType={value}/> },
              { id: 'companyName', filterable: true, label: 'Sender', toggleable: true, getOptions: getCompanyNames, width: '10em'},
              { id: 'partnerCompanyName', filterable: true, label: 'Receiver', toggleable: true, getOptions: getCompanyNames, width: '10em' },
              { id: 'statusDescription', filterable: true, label: 'Status', getOptions: getStatusOptions, template: (value, row, addFilter) => (<Status description={value} onClick={() => addFilter(isNaN(value) ? {operator: 'eq', value: value, field: 'statusDescription'} : {operator: 'eq', value: row.status, field: 'status'} )}/>), width: "12em", stopPropagation: true },
              { id: 'subStatusDescription', filterable: true, hidden: true, toggleable: true, label: 'SubStatus' },
              { id: 'source', filterable: true, hidden: true, toggleable: true, label: 'System' },
              { command: 'commands', stopPropagation: 'true', width: '7em', template: (value, row) => (
                  <div style={{display: 'flex', justifyContent: 'flex-end', paddingRight: '32px'}}>
                    <Button size='small' variant='outlined' onClick={() => updateFieldsFromDocument(row.id, docType)}>Select</Button>
                  </div>
              )}
          ],
          filter: {field: 'documentType', operator: 'eq', value: docType},
          order: 'desc',
          orderBy: 'internalId',
          pageSize: 5,
          pageSizes: [5],
          term: state.term,
          title: `Select ${docType}`,
          actions: <div style={{marginBottom: 'auto', marginTop: 'auto', display: 'flex', flex: 1}}>
            <div>
                <SearchBox placeholder={`Search ${docType.toLowerCase()}s`} onEnter={() => setState({term: state.termValue})} handleChange={(e) => setState({termValue: e.currentTarget.value})} term={state.termValue}/>
            </div>
            </div>
      };
    };

    return <Form
    onSubmit={handleSubmit}
    initialValues={state.profile}
    mutators={{
      // potentially other mutators could be merged here
      ...arrayMutators,
      updateFieldList: updateFieldList
    }}
    decorators={[documentTypeChecker, filterValueChecker, dateCalculator]}
    subscription={{invalid: true, pristine: true}}
    render={({ handleSubmit, pristine, invalid, form: {mutators} }) => (
      <form onSubmit={handleSubmit} autoComplete="off">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <Card>
                <Alert severity="info">
                    <Typography style={{textAlign: 'left'}}>The date filters on this page use your timezone, and the dates in the files are in UTC time. Shared reports will be run using your local timezone.</Typography>
                </Alert>
            </Card>
        </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{'Profile'}</Toolbar>
                    <CardContent>
                        <FormSpy subscription={{values: true}}>
                        {({values}) => <><div style={{display: 'flex'}}>
                        <ExtendedSelectAny innerRef={profileSelectRef} variant='outlined' style={{flex: '1 1 auto', display: 'flex'}} placeholder='Select profile...' value={state.profile.Name} onChange={onProfileSelect} options={state.profiles.map((r: any) => {return {label: r.Name, value: r.Name}})}/>
                        <DownloadButton disabled={!state.profile.Name || isDefault(state.profile.Name) || !(!state.profile.PartnerCompanyName)} style={{marginRight: '1em', marginLeft: '1em'}} variant='outlined' onClick={() => showSave(values)}>Save</DownloadButton>
                        <Button style={{marginRight: '1em'}} variant='outlined' onClick={showSaveAs}>Save As</Button>
                        <Button disabled={!state.profile.Name || isDefault(state.profile.Name) || !(!state.profile.PartnerCompanyName) || (!props.isRetailer && !(!state.profile.Id))} style={{marginRight: '1em'}} onClick={() => setState({showShare: true, shareValues: values})} variant='outlined'>Share</Button>
                        <Button disabled={!state.profile.Name || isDefault(state.profile.Name) || !(!state.profile.PartnerCompanyName)} style={{marginRight: '1em'}} variant='outlined' onClick={() => setState({confirmDelete: true})}>Delete</Button>
                        <Button style={{marginRight: '1em'}} variant='outlined' onClick={() => exportProfileFile(values)}>Save to file</Button>
                        <Button variant='outlined' onClick={openImportDialog}>Load from file</Button>
                        </div>
                        <div style={{textAlign: 'left', marginTop: '0.5em'}}>
                        {state.profile.Description}
                        </div></>}
                        </FormSpy>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{'Output Format'}</Toolbar>
                    <CardContent>
                        <div style={{display: 'flex'}}>
                            <div>
                                <RadioGroup style={{flexDirection: 'row'}} value={state.fileType} onChange={(e) => setState({fileType: e.target.value})}>
                                    {exportTypes.map(r => <FormControlLabel key={r.value} value={r.value} control={<Radio />} label={r.label}/>)}
                                </RadioGroup>
                            </div>
                            <FormSpy subscription={{values: true}}>
                            {({values}) => <div style={{lineHeight: '42px'}}>
                                <DownloadButton disabled={!values.Fields || values.Fields.length <= 0 || values.Fields.every(r => !r.field)} onClick={handleSubmit} onError={(e) => {setState({showError: true, error: e})}} style={{marginRight: '1em'}} variant='contained' color='primary'>Download</DownloadButton>
                            </div>}
                            </FormSpy>
                        </div>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{display: 'flex'}}>
                <Card style={{flex: '1 1 auto'}}>
                    <Toolbar className='lbtoolbar'>{'Document Filter'}</Toolbar>
                    <CardContent>
                        <FieldArray name="Filter" subscription={{}}>
                          {({ fields }) => (
                            <>
                            <FormSpy subscription={{ values: true }}>
                                {({ values }) => (!values.Filter || values.Filter.filter(r => (r.field === "daterange" || r.field === "SenderId" || r.field === "ReceiverId") && !(!r.value)).length === 0) && 
                                <Alert severity="info">
                                    <Typography style={{textAlign: 'left'}}>To optimize your reporting experience, we recommend applying at least one of the following filters: Sender, Receiver, Date Range.</Typography>
                                </Alert>
                                }
                            </FormSpy>
                            <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{width: '15em', paddingTop: 0, paddingBottom: 0}}>Field</TableCell>
                                    <TableCell style={{width: '12em', paddingTop: 0, paddingBottom: 0}} padding="none">Operator</TableCell>
                                    <TableCell style={{paddingTop: 0, paddingBottom: 0}}>Value</TableCell>
                                    <TableCell padding="none" style={{width: '8em', paddingTop: 0, paddingBottom: 0}}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            <TableRow>
                              <TableCell style={{paddingTop: 0, paddingBottom: 0}}><MuiTextField disabled value='Document Type'/></TableCell>
                              <TableCell padding="none" style={{paddingTop: 0, paddingBottom: 0}}><MuiTextField disabled value='is'/></TableCell>
                              <TableCell style={{paddingTop: 0, paddingBottom: 0}}><Field component={Select} controlled style={{textAlign: 'left', width: '100%'}} name='DocumentType' validate={onDocTypeChange} options={documentTypes.map(r => ({label: r, value: r}))}/></TableCell>
                              <TableCell style={{paddingTop: 0, paddingBottom: 0}}></TableCell>
                            </TableRow>
                              {fields.map((name, index) => (
                                <TableRow key={name}>
                                <TableCell style={{paddingTop: 0, paddingBottom: 0}}>
                                  <Field component={Select} controlled style={{textAlign: 'left', width: '100%'}} placeholder='Select field...' name={`${name}.field`} options={filterFields.map(r => ({label: r.FieldTitle, value: r.FieldName}))}/>
                                </TableCell>
                                  <TableCell padding="none" style={{paddingTop: 0, paddingBottom: 0}}>
                                  <IfSet field={`Filter[${index}].field`}>
                                  {({value}) =>
                                      <Field component={Select} controlled style={{textAlign: 'left', width: '100%'}} name={`${name}.operator`} options={getFieldOperators(value)}/>
                                  }
                                  </IfSet>
                                  </TableCell>
                                  <TableCell style={{paddingTop: 0, paddingBottom: 0}}>
                                  <IfSet field={`Filter[${index}].field`}>
                                  {({value: fieldName}) => <IfSet field={`Filter[${index}].operator`}>
                                    {({value: op}) =>
                                      <FilterValueField name={name} fieldName={fieldName} partnerOptions={op === "ingroup" ? state.partnerGroupOptions : state.partnerOptions} statusOptions={state.statusOptions}/>
                                    }
                                  </IfSet>}
                                  </IfSet>
                                  </TableCell>
                                  <TableCell padding="none" style={{paddingTop: 0, paddingBottom: 0}}><Button variant='outlined' onClick={() => fields.remove(index)}>Remove</Button></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                            </Table>
                            <Button variant='outlined' onClick={() => fields.push({ field: '', operator: 'eq', value: '' })} style={{marginTop: '0.5em'}}>Add</Button>
                            </>
                          )}
                        </FieldArray>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{display: 'flex'}}>
                <Card style={{flex: '1 1 auto'}}>
                    <Toolbar className='lbtoolbar'>{'Fields to Export'}</Toolbar>
                    <CardContent>
                        <FieldArray name="Fields" subscription={{}}>
                        {({ fields }) => (
                            <>
                            <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" style={{width: '3em'}}>Order</TableCell>
                                    <TableCell>Input Field/Expression</TableCell>
                                    <TableCell>Output Column</TableCell>
                                    <TableCell>Data Sample<Tooltip title={'Click on Add All Fields From Order at the bottom of this page to select a document and generate Data Sample fields'}>
                                        <InfoOutlinedIcon style={{fontSize: '16px', verticalAlign: 'middle', marginLeft: '6px', color: 'rgba(0,0,0,0.54)'}} />
                                    </Tooltip></TableCell>
                                    <TableCell padding="none" style={{width: '8em'}}></TableCell>
                                </TableRow>
                            </TableHead>
                            <SortableList
                              helperClass={'tableDragElement'}
                              helperContainer={getTableContainer}
                              useDragHandle={true}
                              items={fields}
                              onSortEnd={sortEnd(fields.move)}
                              onSortStart={sortStart}
                              options={state.documentFieldOptions}
                              sampleData={state.sampleData}
                            />
                            </Table>
                            <div style={{marginTop: '0.5em'}}>
                                <Button variant='outlined' onClick={() => fields.push({ field: '', alias: '' })}>Add</Button>
                                <FormSpy subscription={{values: {DocumentType: true}}}>
                                {({ values }) =>
                                <Button variant='outlined' onClick={() => addFieldsFromDocument(mutators.updateFieldList, values)} style={{marginLeft: '0.5em'}}>Add All Fields From {values.DocumentType}</Button>
                                }
                                </FormSpy>
                            </div>
                            </>
                          )}
                        </FieldArray>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
        <Dialog open={state.showDownloading} onClose={() => {setState({showDownloading: false})}}>
            <Toolbar className='lbtoolbar'>Your Report is Being Processed</Toolbar>
            <DialogContent>
                <Typography>The report you requested is currently being generated. Depending on the file size, this may take a few minutes. You can review the report once the download is complete by clicking on the download icon in your browser toolbar. There is no page in Logicbroker to download this report from.</Typography>
                <Typography style={{marginTop: '1em'}}>Feel free to continue using Logicbroker in the meantime.</Typography>
            </DialogContent>
        </Dialog>
        <Dialog open={state.showError} onClose={() => {setState({showError: false})}}>
            <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
            <DialogContent><Typography>{state.error}</Typography></DialogContent>
        </Dialog>
        <Dialog open={state.showImport} onClose={() => {setState({showImport: false})}} maxWidth='md' fullWidth={true}>
            <Toolbar className='lbtoolbar'>{'Import Profile'}</Toolbar>
            <DialogContent>
                <MuiTextField style={{width: '100%', marginBottom: '1em'}} placeholder='New profile name' value={state.newProfileName} onChange={(e) => setState({newProfileName: e.target.value})}/>
                <DropZone accept=".json, .txt" style={{width: '100%', height: '10em'}} onDrop={onDrop}>
                {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }) => {
                    if (isDragAccept || isDragReject) {
                      return "Drop here to upload this file.";
                    }
                    return state.importFile ? state.importFile.name : 'Drop an export profile here or click to choose a file.';
                  }}
                </DropZone>
            </DialogContent>
            <DialogActions><Button color='primary' disabled={!state.newProfileName || state.disableImport || !state.importFile} onClick={() => importFile(state.newProfileName, state.importFile)}>Import</Button></DialogActions>
        </Dialog>
        <Dialog open={state.showDocumentPicker} onClose={() => setState({showDocumentPicker: false})} maxWidth='lg' fullWidth={true}>
            <FormSpy subscription={{ values: {DocumentType: true} }}>
            {({ values }: any) => state.showDocumentPicker && <TypedEnhancedTable config={getSearchConfig(values.DocumentType)} />}
            </FormSpy>
        </Dialog>
        <FormSpy subscription={{ values: true }}>
        {({ values }) => <>
          <SaveAsDialog open={state.showSaveAs} onClose={() => setState({showSaveAs: false})} onSaveAs={onSaveAs} profileType='Export' values={values}/>
          <SaveDialog open={state.showSave} onClose={() => setState({showSave: false})} onSave={onSave} profileType='Export' values={values}/>
          <DeleteDialog open={state.confirmDelete} onClose={() => setState({confirmDelete: false})} onDelete={onDelete} profileType='Export' values={state.profile}/>
          <ShareDialog isRetailer={props.isRetailer} open={state.showShare} onClose={() => setState({showShare: false})} onShare={shared} profileType='Export' values={state.shareValues}/>
          </>}
        </FormSpy>
        </form>
    )}/>;
}

export { DocumentFields, getDownloadLink };
export default AdvancedExport;
